import {
  GetOrderQuery,
  type IGetOrderQueryResponse,
} from '@fhs-legacy/frontend/src/remote/queries/order';
import { client as legacyClient } from '@fhs-legacy/frontend/src/state/graphql/client';
import logger from '@fhs-legacy/frontend/src/utils/logger';
import { IServerOrder } from '@rbi-ctg/menu';

import { ORDER_SUBMITTED_STATUSES } from '../order-status';

const RECOVERY_WINDOW_MS = 90000;
const RETRY_DELAY_MS = 3000;

type SubmittedOrderLookup =
  | { status: 'submitted'; order: IServerOrder }
  | { status: 'notSubmitted' }
  | { status: 'unavailable' };

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export async function recoverSubmittedOrder(rbiOrderId: string): Promise<SubmittedOrderLookup> {
  const deadline = Date.now() + RECOVERY_WINDOW_MS;

  while (Date.now() < deadline) {
    try {
      const response = await legacyClient.query<IGetOrderQueryResponse>({
        query: GetOrderQuery,
        variables: { rbiOrderId },
        fetchPolicy: 'network-only',
      });

      const order = response.data?.order;
      if (order?.status) {
        if (ORDER_SUBMITTED_STATUSES.has(order.status)) {
          return { status: 'submitted', order };
        }
        // Definitive pre-commit status — safe to treat as not submitted.
        return { status: 'notSubmitted' };
      }
      // Null/missing can be short-lived after a successful commit (read propagation).
      // Keep polling; deadline yields unavailable so we do not allow a duplicate retry.
    } catch (error) {
      logger.warn({
        message: '[RecoverSubmittedOrder] Order status lookup failed, still retrying',
        rbiOrderId,
        error: error instanceof Error ? error.message : String(error),
      });
    }

    await delay(RETRY_DELAY_MS);
  }

  return { status: 'unavailable' };
}
